import classNames from "./Meeting.module.css";
import { useLeaveMeeting, useMeeting } from "../../../hooks/meeting";
import {
  LiveKitRoom,
  RoomAudioRenderer,
  useConnectionState,
  useLocalParticipant,
  usePagination,
  useRemoteParticipants,
  useRoomContext,
  useTracks,
} from "@livekit/components-react";
import { usePublisherToken } from "../../../hooks/token";
import { useAxios } from "../../../hooks/axios";
import useUser, {
  useRenderLanguage,
  useUserMetadata,
} from "../../../hooks/user";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Captions from "../../common/Captions";
import {
  LocalVideoTrack,
  RemoteParticipant,
  RemoteTrackPublication,
  RoomEvent,
  Track,
} from "livekit-client";
import WaitingRoom, { WaitingRoomChoices } from "./WaitingRoom";
import Controls from "./Controls";
import ResizePanelVertical from "./ResizePanelVertical";
import Participants from "./Participants";
import Loader from "../../common/Loader";
import {
  useAudioDeviceId,
  useBlurFilter,
  useVideoDeviceId,
} from "../../../hooks/media";
import ResizePanelHorizontal from "./ResizePanelHorizontal";
import Chat from "../../common/Chat";
import MeetingAttendees from "./MeetingAttendees";
import { useRecordingToast } from "../../../hooks/recording";
import {
  useLivekitDisconnectToast,
  usePollRoomMinutesRemaining,
  useRoom,
  useWakeLock,
} from "../../context/room/room";
import { useDefaultSettings } from "../../../hooks/settings";
import { useIdleTimer } from "react-idle-timer";
import { toast, Id as ToastId } from "react-toastify";
import FloatingEmojis from "./FloatingEmojis";
import { useIsMobile } from "../../../hooks/layout";
import { useChatMessages, useListenForChatMessages } from "../../../hooks/chat";
import api from "../../../hooks/api-new";

function Translator() {
  useUserMetadata();
  useDefaultSettings();
  usePollRoomMinutesRemaining();
  useListenForChatMessages();

  const { cameraTrack: cameraTrackPublication } = useLocalParticipant();
  useBlurFilter(
    cameraTrackPublication?.videoTrack as LocalVideoTrack | undefined
  );

  const cameraTracks = useTracks(
    [{ source: Track.Source.Camera, withPlaceholder: true }],
    { updateOnlyOn: [RoomEvent.ActiveSpeakersChanged], onlySubscribed: false }
  );

  const participantCameraTracks = useMemo(() => {
    return cameraTracks.filter((t) => !t.participant.isAgent);
  }, [cameraTracks]);

  const screenShareTracks = useTracks(
    [{ source: Track.Source.ScreenShare, withPlaceholder: false }],
    { updateOnlyOn: [RoomEvent.ActiveSpeakersChanged], onlySubscribed: false }
  );

  const isMobile = useIsMobile();

  const pagination = usePagination(
    isMobile ? 2 : screenShareTracks.length > 0 ? 3 : 9,
    participantCameraTracks
  );

  const [user, setUser] = useUser();

  const room = useRoomContext();
  const connectionState = useConnectionState();
  const [meeting, setMeeting] = useMeeting();
  useRecordingToast({
    inProgressToast: (
      <div className="Toast">
        <div>{"Recording in progress"}</div>
      </div>
    ),
    stopToast: (
      <div className="Toast">
        <div>{"Recording stopped"}</div>
      </div>
    ),
  });

  useLivekitDisconnectToast(
    <div className="Toast ErrorToast">
      <div>
        {"Your connection is unstable. Hang tight, we're reconnecting you..."}
      </div>
    </div>
  );

  // Subscribe to tracks
  useEffect(() => {
    function handlePublication(
      publication: RemoteTrackPublication,
      participant: RemoteParticipant
    ) {
      // Handle ai voice subscribing in voice component, ignore here
      if (publication.trackName.includes("ai-voice")) {
        return;
      }

      // Subscribe to all non ai voice audio tracks
      if (publication.kind === "audio") {
        publication.setSubscribed(true);
      }

      // Only subscribe to video tracks when in page (use pagination) or screen sharing
      if (publication.kind === "video") {
        publication.setSubscribed(
          !!pagination.tracks.find(
            (t) => t.participant.sid === participant.sid
          ) || publication.track?.source === Track.Source.ScreenShare
        );
      }
    }

    if (connectionState === "connected") {
      room.remoteParticipants.forEach((participant) => {
        participant.trackPublications.forEach((publication) => {
          handlePublication(publication, participant);
        });
      });
    }

    function onTrackPublished(
      publication: RemoteTrackPublication,
      participant: RemoteParticipant
    ) {
      if (connectionState === "connected") {
        handlePublication(publication, participant);
      }
    }

    room.on("trackPublished", onTrackPublished);
    return () => {
      room.off("trackPublished", onTrackPublished);
    };
  }, [room, connectionState, pagination]);

  // When 2 or more participants (except agent), set local participant to speaker to translate audio
  const remoteParticipants = useRemoteParticipants();
  const numParticipants = useMemo(() => {
    return remoteParticipants.filter((p) => !p.isAgent).length + 1;
  }, [remoteParticipants]);
  useEffect(() => {
    setUser((prevState) => ({
      ...prevState,
      metadata: {
        ...prevState.metadata,
        participantType: numParticipants >= 2 ? "speaker" : "viewer",
      },
    }));
  }, [numParticipants, setUser]);

  const axios = useAxios();
  const updateSessionRequest = api.useUpdateSessionRequest(axios);
  const onAction = useCallback(() => {
    // Set last active at in session
    updateSessionRequest({ client_id: user.livekitId });
  }, [updateSessionRequest, user.livekitId]);

  const idleToastRef = useRef<ToastId>();
  const onIdle = useCallback(() => {
    if (numParticipants <= 1) {
      if (!idleToastRef.current) {
        idleToastRef.current = toast(
          <div className="Toast">
            <div>
              {"Are you still there?"}
              <button
                onClick={() => {
                  toast.dismiss(idleToastRef.current);
                }}
              >
                Yes
              </button>
            </div>
          </div>,
          {
            autoClose: false,
            onClose: () => {
              idleToastRef.current = undefined;
            },
          }
        );
      }
    }
  }, [numParticipants]);

  useEffect(() => {
    return () => {
      toast.dismiss(idleToastRef.current);
    };
  }, []);

  useIdleTimer({
    onAction: onAction,
    onIdle: onIdle,
    timeout: 30 * 60 * 1000,
    throttle: 30 * 1000,
  });

  const renderLanguage = useRenderLanguage();

  const [numUnreadMessages, setNumUnreadMessages] = useState(0);
  const [messages] = useChatMessages();
  const numPreviousMessages = useRef(0);
  useEffect(() => {
    const numNewMessages = Math.max(
      messages.length - numPreviousMessages.current,
      0
    );
    if (numNewMessages > 0 && meeting.layout.panels.side !== "chat") {
      setNumUnreadMessages((prev) => prev + numNewMessages);
    }
    numPreviousMessages.current = messages.length;
  }, [messages.length, meeting.layout.panels.side]);

  useEffect(() => {
    if (meeting.layout.panels.side === "chat") {
      setNumUnreadMessages(0);
    }
  }, [meeting.layout.panels.side, messages.length]);

  return (
    <div className={classNames.Meeting}>
      <RoomAudioRenderer />
      <ResizePanelHorizontal>
        <div className={classNames.MeetingPanel}>
          <ResizePanelVertical
            minTopHeightInPixels={!isMobile ? 400 : 200}
            minBottomHeightInPixels={50}
          >
            {meeting.layout.panels.participants && (
              <Participants
                participantCameraTracks={participantCameraTracks}
                screenShareTracks={screenShareTracks}
                pagination={pagination}
              />
            )}
            {meeting.layout.panels.captions && (
              <div
                className={classNames.Captions}
                style={
                  meeting.layout.panels.participants
                    ? { marginTop: "-24px", paddingBottom: "0px" }
                    : undefined
                }
              >
                <Captions renderLanguage={renderLanguage} maxWidth="75%" />
              </div>
            )}
          </ResizePanelVertical>
        </div>
        {meeting.layout.panels.side === "chat" && (
          <Chat
            canAskQuestions={true}
            mode={"chat"}
            className={classNames.Chat}
            open={meeting.layout.panels.side === "chat"}
            onOpenChange={(open) => {
              setMeeting((prevState) => ({
                ...prevState,
                layout: {
                  ...prevState.layout,
                  panels: {
                    ...prevState.layout.panels,
                    side: open ? "chat" : undefined,
                  },
                },
              }));
            }}
          />
        )}
        {meeting.layout.panels.side === "participants" && <MeetingAttendees />}
      </ResizePanelHorizontal>
      <FloatingEmojis />
      <Controls numUnreadMessages={numUnreadMessages} />
    </div>
  );
}

function InMeeting(props: { choices: WaitingRoomChoices }) {
  useWakeLock();
  const axios = useAxios();
  const { token, error: tokenError } = usePublisherToken(axios);
  const leaveMeeting = useLeaveMeeting();

  useEffect(() => {
    if (tokenError && tokenError.response?.data.message === "IDLE") {
      leaveMeeting();
      toast(
        <div className="Toast">
          {"You were removed from the meeting after being idle for too long."}
        </div>,
        { autoClose: false }
      );
    }
  }, [leaveMeeting, tokenError]);

  const [audioDeviceId] = useAudioDeviceId();
  const [videoDeviceId] = useVideoDeviceId();

  if (!token) {
    return <Loader />;
  }

  return (
    <LiveKitRoom
      audio={
        props.choices.audio
          ? {
              deviceId: audioDeviceId,
              echoCancellation: true,
            }
          : false
      }
      video={props.choices.video ? { deviceId: videoDeviceId } : false}
      connect={true}
      token={token}
      connectOptions={{ autoSubscribe: false }}
      serverUrl={import.meta.env.VITE_LIVEKIT_URL}
    >
      <Translator />
    </LiveKitRoom>
  );
}

export default function Meeting() {
  const [, setUser] = useUser();
  useEffect(() => {
    setUser((prevState) => ({
      ...prevState,
      metadata: {
        ...prevState.metadata,
        // Join the meeting as a viewer first, set to speaker once others in room
        participantType: "viewer",
      },
    }));
  }, [setUser]);

  const [waitingRoomChoices, setWaitingRoomChoices] =
    useState<WaitingRoomChoices>({
      audio: true,
      video: true,
    });

  const { room, loading } = useRoom();
  const [meeting] = useMeeting();
  const leaveMeeting = useLeaveMeeting();

  useEffect(() => {
    return () => {
      leaveMeeting();
    };
  }, [leaveMeeting]);

  if (!room || loading) {
    return <Loader />;
  }

  if (!meeting.joined) {
    return (
      <WaitingRoom
        choices={waitingRoomChoices}
        onJoin={setWaitingRoomChoices}
      />
    );
  }

  return <InMeeting choices={waitingRoomChoices} />;
}
